<template>
  <div class="admit-card-wrapper">
    <NoticeBar :visible="noticeVisible" :message="noticeMessage" :type="noticeType" :actions="noticeActions" @close="noticeVisible=false" />
    <div class="admit-page" v-if="ticket">
      <div class="toolbar no-print">
        <button class="btn" @click="goBack">返回</button>
        <button class="btn primary" @click="doPrint">打印</button>
        <button class="btn success" @click="saveAsImage">保存为照片</button>
      </div>
      <div class="sheet" ref="sheetRef">
        <div class="left-panel">
        <h1 class="main-title">准考证</h1>
        <h2 class="org-name">{{ companyName }}</h2>
        <h3 class="exam-name">{{ examTitle }}</h3>

        <div class="info-grid">
          <div class="info-row">
            <span class="label">考生姓名：</span>
            <span class="value">{{ candidateName }}</span>
          </div>
          <div class="info-row">
            <span class="label">身份证号：</span>
            <span class="value">{{ idNumber }}</span>
          </div>
          <div class="info-row">
            <span class="label">考场号：</span>
            <span class="value">{{ ticket.room ? String(ticket.room).padStart(3, '0') : '-' }}</span>
          </div>
          <div class="info-row">
            <span class="label">准考证号：</span>
            <span class="value">{{ admitCode }}</span>
          </div>
          <div class="info-row">
            <span class="label">座位号：</span>
            <span class="value">{{ ticket.seat ? String(ticket.seat).padStart(3, '0') : '-' }}</span>
          </div>
          <div class="info-row full-width">
            <span class="label">报考岗位：</span>
            <span class="value">{{ ticket.position || '-' }}</span>
          </div>
          <div class="info-row full-width">
            <span class="label">考试科目：</span>
            <span class="value">
              <template v-if="subjectsList.length > 0">
                <div v-for="(subject, idx) in subjectsList" :key="idx" class="subject-item">
                  {{ subject.name }}{{ subject.date ? '：' + subject.date : '' }} {{ subject.period }} {{ subject.start_time }}-{{ subject.end_time }}
                </div>
              </template>
              <template v-else>{{ examTimeText || '-' }}</template>
            </span>
          </div>
          <div class="info-row full-width">
            <span class="label">笔试地点：</span>
            <span class="value">{{ ticket.venue || '-' }}<br /><span class="venue-detail" v-if="ticket.venue_detail">（{{
              ticket.venue_detail }}）</span></span>
          </div>
        </div>

        <div class="footer-note">
          <p>考生应持准考证和身份证进入考场</p>
          <p>请考生在考试前务必阅读考生须知</p>
        </div>
      </div>

      <div class="right-panel">
        <h2 class="notice-header">考生须知</h2>
        <div class="notice-content">
          <div v-for="(line, index) in noticeLines" :key="index" class="notice-item">
            {{ line }}
          </div>
        </div>
        </div>
      </div>
    </div>
    <div class="empty" v-else>
      <div v-if="loading">正在加载...</div>
      <div v-else>{{ error || '暂无可打印的准考证' }}</div>
    </div>
  </div>
</template>

<script>
import { listMyTickets, getExam } from '../api/exams'
import NoticeBar from './NoticeBar.vue'
import html2canvas from 'html2canvas'
import domtoimage from 'dom-to-image-more'
export default {
  name: 'AdmitCardPrint',
  components: { NoticeBar },
  props: { examId: { type: [String, Number], default: '' } },
  data() {
    return { ticket: null, exam: null, loading: false, error: '', noticeVisible: false, noticeMessage: '', noticeType: 'info', noticeActions: [] }
  },
  async mounted() {
    await this.fetch()
  },
  computed: {
    examTitle() { return this.exam?.title || this.ticket?.exam_title || '-' },
    companyName() {
      // 优先使用准考证上记录的公司名称（发布时的快照）
      if (this.ticket && this.ticket.company_name) return this.ticket.company_name
      // 其次使用考试配置中的准考证公司名称
      if (this.exam && this.exam.ticket_company_name) return this.exam.ticket_company_name
      // 最后尝试使用考试关联的报名表中的公司（如果有）- 但按需求应避免使用用户填写的
      return ''
    },
    candidateName() { return this.ticket?.submission_info?.real_name || '-' },
    idNumber() { return this.ticket?.submission_info?.id_number || '-' },
    admitCode() { return this.ticket?.admit_code || this.ticket?.ticket_number || '-' },
    examTimeText() {
      return this.ticket?.exam_time_text || this.formatRange(this.exam?.start_time, this.exam?.end_time)
    },
    venueText() {
      const v = this.ticket?.venue || '-'
      const room = this.ticket?.room ? `（考场${String(this.ticket.room).padStart(3, '0')}，座位${String(this.ticket.seat).padStart(3, '0')}）` : ''
      return `${v}${room}`
    },
    noticeLines() {
      if (!this.ticket?.notes) return []
      return this.ticket.notes.split('\n').filter(line => line.trim())
    },
    subjectsList() {
      // 从考试中获取考试科目（支持按岗位分组的新格式）
      const subjectsJson = this.exam?.ticket_subjects
      if (!subjectsJson) return []
      try {
        let subjectsData = subjectsJson
        if (typeof subjectsJson === 'string') {
          subjectsData = JSON.parse(subjectsJson)
        }
        
        // 检查是否是新格式（按岗位分组）
        if (subjectsData && typeof subjectsData === 'object' && subjectsData.byPosition) {
          const candidatePosition = this.ticket?.position || ''
          const positions = subjectsData.positions || []
          
          // 查找考生岗位对应的科目
          if (candidatePosition && positions.length > 0) {
            const posIdx = positions.indexOf(candidatePosition)
            if (posIdx >= 0 && subjectsData.byPosition[posIdx]) {
              const posSubjects = subjectsData.byPosition[posIdx].filter(s => s.name && s.name.trim())
              if (posSubjects.length > 0) return posSubjects
            }
          }
          
          // 如果没有找到岗位对应的科目，使用通用科目
          if (subjectsData.common && subjectsData.common.length > 0) {
            return subjectsData.common.filter(s => s.name && s.name.trim())
          }
          return []
        }
        
        // 简单数组格式（当前管理端保存的格式）
        if (Array.isArray(subjectsData)) {
          // 当前格式: { name, time_period, start_time, end_time, pass_score, position }
          // 从 exam_time_text 中提取日期
          let examDate = ''
          const timeText = this.ticket?.exam_time_text || this.exam?.ticket_time_text || ''
          const dateMatch = timeText.match(/(\d{4}年\d{1,2}月\d{1,2}日)/)
          if (dateMatch) {
            examDate = dateMatch[1]
          }
          
          // 筛选当前考生岗位对应的科目
          const candidatePosition = this.ticket?.position || ''
          const filteredSubjects = candidatePosition 
            ? subjectsData.filter(s => s.position === candidatePosition || !s.position)
            : subjectsData
          
          // 转换为显示格式
          return filteredSubjects.map(s => ({
            name: s.name || '笔试',
            date: examDate,
            period: s.time_period || '',
            start_time: s.start_time || '',
            end_time: s.end_time || ''
          })).filter(s => s.start_time && s.end_time)
        }
        
        return []
      } catch (e) {
        return []
      }
    }
  },
  methods: {
    async fetch() {
      this.loading = true
      this.error = ''
      try {
        let list = []
        try {
          list = await listMyTickets()
        } catch (_) { list = [] }
        this.ticket = Array.isArray(list) ? list.find(t => String(t.exam_id) === String(this.examId)) : null
        // 兜底：直接按考试ID查询已发布的准考证
        if (!this.ticket && this.examId) {
          try {
            const res = await fetch(`/api/exam/tickets/?exam_id=${encodeURIComponent(this.examId)}`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('user_access_token') || ''}` }
            })
            if (res.ok) {
              const arr = await res.json()
              const tmp = Array.isArray(arr) ? arr : (arr.results || [])
              this.ticket = tmp.find(t => String(t.exam_id || t?.exam?.id || '') === String(this.examId)) || null
            }
          } catch (_) { }
        }
        if (this.examId) this.exam = await getExam(this.examId)
        if (!this.ticket) this.error = '暂无可打印的准考证（请确认已发布）'
      } catch (e) {
        console.warn('加载准考证失败', e)
        this.ticket = null
        this.error = '准考证加载失败，请稍后重试'
      } finally { this.loading = false }
    },
    goBack() {
      try {
        localStorage.setItem('redirect_to_ticket_home', '1')
      } catch (e) { /* noop */ }
      window.location.hash = '#home'
    },
    doPrint() {
      window.print()
    },
    async saveAsImage() {
      const target = this.$refs.sheetRef
      if (!target) { this.showNotice('无法定位准考证区域', 'error'); return }

      this.showNotice('正在生成图片，请稍候...', 'info', [], 0)
      const fileName = `${this.examTitle || '准考证'}_${this.candidateName}.png`

      const downloadBlob = (blob) => {
        if (!blob) { this.showNotice('生成图片失败', 'error'); return }
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        a.style.display = 'none'
        document.body.appendChild(a)
        a.click()
        setTimeout(() => {
          document.body.removeChild(a)
          URL.revokeObjectURL(url)
        }, 100)
        this.showNotice('图片已保存', 'success')
      }

      // 方法1: 尝试使用 dom-to-image-more
      try {
        console.log('尝试使用 dom-to-image-more...')
        const blob = await domtoimage.toBlob(target, {
          bgcolor: '#ffffff',
          quality: 1,
          scale: 2,
          style: {
            margin: '0',
            padding: '0'
          }
        })
        if (blob) {
          downloadBlob(blob)
          return
        }
      } catch (err1) {
        console.warn('dom-to-image 失败:', err1)
      }

      // 方法2: 尝试使用 html2canvas
      try {
        console.log('尝试使用 html2canvas...')
        const canvas = await html2canvas(target, { 
          scale: 2, 
          useCORS: true, 
          allowTaint: true,
          backgroundColor: '#ffffff', 
          logging: false
        })
        if (canvas && canvas.toBlob) {
          canvas.toBlob(downloadBlob, 'image/png')
          return
        } else if (canvas) {
          const dataUrl = canvas.toDataURL('image/png')
          const arr = dataUrl.split(',')
          const mime = arr[0].match(/:(.*?);/)[1]
          const bstr = atob(arr[1])
          let n = bstr.length
          const u8arr = new Uint8Array(n)
          while (n--) u8arr[n] = bstr.charCodeAt(n)
          const blob = new Blob([u8arr], { type: mime })
          downloadBlob(blob)
          return
        }
      } catch (err2) {
        console.warn('html2canvas 失败:', err2)
      }

      this.noticeVisible = false
      this.showNotice('保存图片失败，请尝试直接截图或打印保存为PDF。', 'error')
    },
    showNotice(message, type = 'info', actions = [], autoHideMs = 2500) {
      this.noticeMessage = message
      this.noticeType = type
      this.noticeActions = actions
      this.noticeVisible = true
      if (autoHideMs > 0) setTimeout(() => { this.noticeVisible = false }, autoHideMs)
    },
    formatRange(s, e) {
      if (!s || !e) return '-'
      const sd = new Date(s), ed = new Date(e)
      const fmt = d => `${d.getFullYear()}年${String(d.getMonth() + 1).padStart(2, '0')}月${String(d.getDate()).padStart(2, '0')}日 ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`
      return `${fmt(sd)} ~ ${fmt(ed)}`
    }
  }
}
</script>

<style scoped>
.no-print {
  display: block;
}

@media print {
  .no-print {
    display: none !important;
  }
}

.admit-page {
  padding: 12px;
}

.toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.btn {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #f7f7f7;
  cursor: pointer;
}

.btn.primary {
  background: #1677ff;
  border-color: #1677ff;
  color: #fff;
}

.btn.success {
  background: #52c41a;
  border-color: #52c41a;
  color: #fff;
}

.sheet {
  display: flex;
  background: #fff;
  border: 1px solid #000;
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  min-height: 600px;
  box-sizing: border-box;
}

.left-panel {
  flex: 1;
  padding: 20px 30px 30px 30px;
  border-right: 1px solid #000;
  display: flex;
  flex-direction: column;
  position: relative;
}

.right-panel {
  flex: 1;
  padding: 30px;
  display: flex;
  flex-direction: column;
}

.main-title {
  text-align: center;
  font-size: 32px;
  font-weight: bold;
  letter-spacing: 12px;
  margin: 20px 0 30px 0;
  color: #000;
}

.org-name {
  text-align: center;
  font-size: 20px;
  font-weight: bold;
  margin: 0 0 10px 0;
  color: #000;
}

.exam-name {
  text-align: center;
  font-size: 20px;
  font-weight: bold;
  margin: 0 0 40px 0;
  color: #000;
}

.info-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: auto;
  padding: 0 10px;
}

.info-row {
  width: 48%;
  margin-bottom: 15px;
  display: flex;
  align-items: flex-start;
  font-size: 16px;
  line-height: 1.5;
}

.info-row.full-width {
  width: 100%;
}

.label {
  font-weight: bold;
  min-width: 85px;
  color: #000;
  flex-shrink: 0;
}

.value {
  color: #000;
  font-weight: 500;
  word-break: break-all;
}

.subject-item {
  margin-bottom: 4px;
}

.subject-item:last-child {
  margin-bottom: 0;
}

.footer-note {
  margin-top: 40px;
  text-align: right;
  font-size: 14px;
  font-weight: bold;
  color: #000;
  line-height: 1.8;
  padding-right: 20px;
}

.footer-note p {
  margin: 0;
}

.notice-header {
  text-align: center;
  font-size: 24px;
  font-weight: bold;
  margin: 20px 0 30px 0;
  letter-spacing: 4px;
  color: #000;
}

.notice-content {
  font-size: 15px;
  line-height: 1.8;
  color: #000;
  text-align: justify;
}

.notice-item {
  margin-bottom: 8px;
}

@media print {
  @page {
    size: A4 landscape;
    margin: 10mm;
  }

  .sheet {
    border: 1px solid #000 !important;
    max-width: none;
    height: 100%;
    min-height: 180mm;
  }

  .left-panel {
    border-right: 1px solid #000 !important;
  }

  body {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
